import React from "react";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import { SelectCheckbox } from "components/common/SelectCheckbox";
import { useDroppable } from "@dnd-kit/core";
import { DeleteDialogSection } from "./DeleteDialogSection";
import { TableItem } from "./TableItem";
import { TransferTable, fadeOutClass } from "./TransferTypes";

interface TrashDropTargetProps {
  tables: TransferTable[];
  isDragActive?: boolean;
  selectedTables: Set<string>;
  onToggleSelection: (tableId: string, e: React.MouseEvent) => void;
  onSelectAll: () => void;
  draggedTableIds: Set<string>;
  fadingOutTableIds?: Set<string>;
}

export const TrashDropTarget: React.FC<TrashDropTargetProps> = ({
  tables,
  isDragActive = false,
  selectedTables,
  onToggleSelection,
  onSelectAll,
  draggedTableIds,
  fadingOutTableIds = new Set(),
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: "trash",
  });

  const showHighlight = isDragActive && isOver;

  const allTablesSelected =
    tables.length > 0 && tables.every((t) => selectedTables.has(t.id));

  return (
    <div ref={setNodeRef} className="h-full min-h-0" id="trash-drop-target">
      <DeleteDialogSection
        icon={
          <DeleteOutlineSharpIcon className="icon-sm transition-colors text-fg-muted-regular" />
        }
        title="Scheduled for Deletion"
        borderStyle="dashed"
        className="h-full min-h-0 p-4"
        isActive={showHighlight}
        titleAction={
          tables.length > 0 ? (
            <SelectCheckbox
              selected={allTablesSelected}
              onToggle={onSelectAll}
              tooltip={{ checked: "Deselect all", unchecked: "Select all" }}
              size="sm"
            />
          ) : undefined
        }
      >
        {tables.length > 0 ? (
          <div>
            {tables.map((table) => (
              <div
                key={table.id}
                className={fadeOutClass(fadingOutTableIds.has(table.id))}
              >
                <TableItem
                  table={table}
                  isSelected={selectedTables.has(table.id)}
                  onToggleSelection={(e) => onToggleSelection(table.id, e)}
                  isDragging={draggedTableIds.has(table.id)}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="transition-colors text-fg-muted-regular">
            {showHighlight
              ? "Drop here to schedule for deletion"
              : "Drag and drop here to schedule for deletion"}
          </div>
        )}
      </DeleteDialogSection>
    </div>
  );
};
